import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SERVICES = [
  {
    icon: 'ri-router-line',
    tag: '01',
    title: 'オフィスIT機器・ネットワーク構築',
    subtitle: 'Office IT & Network',
    desc: 'セキュリティを前提とした、安全で快適なオフィス環境を構築します。ITとセキュリティを切り離さず、トータルで最適化します。',
    items: ['PC・周辺機器', '複合機', '防犯カメラ', '社内LAN／ネットワーク施工', 'Wi-Fi環境構築'],
    img: 'https://public.readdy.ai/ai/img_res/3f360dade643cdcdbab3134a636b74dd.jpg',
  },
  {
    icon: 'ri-shield-keyhole-line',
    tag: '02',
    title: '情報セキュリティ対策',
    subtitle: 'Information Security',
    desc: '企業の情報資産を守るため、現実的かつ運用しやすいセキュリティ対策をご提案します。「何も起きてない」状態は、安全とは限りません。見えないリスクを"見える化"することから始めます。',
    items: ['UTM', 'セキュアブラウザ', 'ウィルス対策ソフト', '不正アクセス対策', '情報漏洩対策'],
    img: 'https://readdy.ai/api/search-image?query=financial%20documents%20spreadsheets%20calculator%20on%20clean%20desk%2C%20professional%20accountant%20tax%20advisor%20working%2C%20modern%20office%20environment%2C%20natural%20light%2C%20organized%20and%20precise%2C%20soft%20blue%20and%20white%20tones&width=600&height=400&seq=srv002&orientation=landscape',
  },
  {
    icon: 'ri-ai-generate',
    tag: '03',
    title: 'IT・DX支援',
    subtitle: 'IT & DX Support',
    desc: 'ITを「導入して終わり」にせず、業務に活用するところまでサポートします。HP作成からAI・DX活用まで、ビジネスのデジタル化を幅広く支援します。',
    items: ['HP作成', 'AI活用コンサルティング', 'DXコンサルティング', '業務効率化支援'],
    img: 'https://readdy.ai/api/search-image?query=modern%20digital%20transformation%20technology%20business%2C%20laptop%20with%20data%20dashboards%20analytics%2C%20clean%20tech%20office%20environment%2C%20minimalist%20design%2C%20sky%20blue%20and%20neutral%20tones%2C%20professional%20setting&width=600&height=400&seq=srv004&orientation=landscape',
  },
];

const PROCESS = [
  { num: '01', title: '無料相談', desc: '現状や課題をヒアリング。方向性のすり合わせを行います。' },
  { num: '02', title: '現状分析', desc: 'オフィスのIT・セキュリティ環境を多角的に診断します。' },
  { num: '03', title: '提案・設計', desc: '課題に最適な機器・対策・ロードマップを提案します。' },
  { num: '04', title: '導入・構築', desc: '機器調達から設置・設定まで一括でサポートします。' },
  { num: '05', title: '運用サポート', desc: 'トラブル対応・定期点検・アップデート管理を継続支援します。' },
];

export default function ServicesPage() {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = sectionRefs.current.map((el) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.add('animate-in');
            obs.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <div className="bg-[#F8FCFF] text-[#1A2E3A]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative h-80 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20Japanese%20business%20office%20interior%20modern%20workspace%2C%20clean%20minimalist%20design%2C%20sky%20blue%20and%20white%20accents%2C%20warm%20natural%20light%20streaming%2C%20architectural%20elegance%2C%20premium%20corporate%20environment&width=1920&height=600&seq=svhero001&orientation=landscape"
            alt="サービス"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#163347]/70" />
        </div>
        <div className="relative z-10 w-full px-10 lg:px-20 pb-14">
          <p className="text-[#7EC8E8] text-xs tracking-[0.4em] mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>SERVICES</p>
          <h1 className="text-white text-3xl lg:text-5xl font-light" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
            サービス一覧
          </h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="px-10 lg:px-20 py-4 flex items-center gap-2 text-xs text-[#5A7A8A] tracking-wider border-b border-[#C8E4F2]">
        <Link to="/" className="hover:text-[#2B8DB8] transition-colors cursor-pointer">ホーム</Link>
        <i className="ri-arrow-right-s-line" />
        <span>サービス</span>
      </div>

      {/* Intro */}
      <section className="py-20 px-10 lg:px-20 text-center">
        <div
          ref={(el) => { sectionRefs.current[0] = el; }}
          className="opacity-0 translate-y-8 transition-all duration-700 max-w-3xl mx-auto"
        >
          <p className="text-[#5A7A8A] text-sm leading-loose tracking-wider">
            株式会社湘南ビジネスサポートは、情報セキュリティを中心にオフィスIT環境をワンストップで支援します。
            機器の選定・導入から日々の運用サポートまで、中小企業のIT担当者の代わりとして伴走し、安全で快適なオフィス環境を実現します。
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="px-10 lg:px-20 pb-24">
        <div className="flex flex-col gap-20">
          {SERVICES.map((service, i) => (
            <div
              key={service.tag}
              ref={(el) => { sectionRefs.current[i + 1] = el; }}
              className={`opacity-0 translate-y-8 transition-all duration-700 flex flex-col ${
                i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-12 items-center`}
            >
              <div className="lg:w-5/12">
                <div className="relative">
                  <div className="w-full h-64 rounded-2xl overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-[#2B8DB8] rounded-2xl flex items-center justify-center">
                    <span className="text-white/70 text-xs tracking-widest" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {service.tag}
                    </span>
                  </div>
                </div>
              </div>
              <div className="lg:w-7/12">
                <p className="text-[#2B8DB8] text-xs tracking-[0.3em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{service.subtitle}</p>
                <h2 className="text-2xl lg:text-3xl font-light text-[#1A2E3A] mb-4 leading-snug" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
                  {service.title}
                </h2>
                <p className="text-[#5A7A8A] text-sm leading-loose tracking-wider mb-6">{service.desc}</p>
                <div className="grid grid-cols-2 gap-2">
                  {service.items.map((item) => (
                    <div key={item} className="flex items-center gap-2 text-[#1A2E3A] text-xs tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2B8DB8] flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#EEF7FC] py-28 px-10 lg:px-20">
        <div
          ref={(el) => { sectionRefs.current[5] = el; }}
          className="opacity-0 translate-y-8 transition-all duration-700"
        >
          <div className="text-center mb-16">
            <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>HOW WE WORK</p>
            <h2 className="text-3xl font-light text-[#1A2E3A]" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
              支援の流れ
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-0 max-w-5xl mx-auto">
            {PROCESS.map((step, i) => (
              <div key={step.num} className="flex-1 relative">
                <div className="text-center px-4">
                  <div className="w-12 h-12 rounded-full border-2 border-[#2B8DB8] flex items-center justify-center mx-auto mb-4 bg-white">
                    <span className="text-[#2B8DB8] text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-[#1A2E3A] mb-2 tracking-wider">{step.title}</h3>
                  <p className="text-[#5A7A8A] text-xs leading-loose">{step.desc}</p>
                </div>
                {i < PROCESS.length - 1 && (
                  <div className="hidden lg:block absolute top-6 -right-2 w-4 h-px bg-[#2B8DB8]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-10 text-center">
        <div
          ref={(el) => { sectionRefs.current[6] = el; }}
          className="opacity-0 translate-y-8 transition-all duration-700"
        >
          <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>CONTACT</p>
          <h2 className="text-3xl font-light text-[#1A2E3A] mb-5" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
            まずは無料相談からはじめましょう
          </h2>
          <p className="text-[#5A7A8A] text-sm tracking-wider mb-10">
            お客様のオフィス環境・予算・課題に応じた最適なプランをご提案いたします。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#2B8DB8] text-white text-xs tracking-widest rounded-full hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            無料相談を申し込む
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />

      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}
